import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, BookOpen, Users, CheckCircle2, Wallet, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClaimInstitute } from "@/components/ClaimInstitute";

const EMPTY = {
  title: "", specialty: "", city: "", state: "", format: "Offline", duration: "", fee: "",
  application_fee: "500", seats: "20", stipend: "", eligibility: "", tags: "", description: "",
  image_url: "https://images.unsplash.com/photo-1550831107-1553da8c8464?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  start_date: "", application_deadline: "",
};
const NEXT = { submitted: "in_review", in_review: "interview", interview: "accepted" };

export default function InstituteDashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState(null);
  const [apps, setApps] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    if (user.role === "institute" && !user.institute_id) { setCourses([]); return; }
    const params = user.role === "institute" ? { institute_id: user.institute_id } : {};
    const [c, a] = await Promise.all([
      api.get("/courses", { params }),
      api.get("/applications/institute"),
    ]);
    setCourses(c.data); setApps(a.data);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveCourse = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form, fee: parseFloat(form.fee) || 0, application_fee: parseFloat(form.application_fee) || 500,
        seats: parseInt(form.seats) || 20,
        eligibility: form.eligibility.split(",").map((s) => s.trim()).filter(Boolean),
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        curriculum: [], documents_required: [], admission_process: ["Submit online application", "Document verification", "Interview", "Admission & payment"],
      };
      await api.post("/courses", payload);
      toast.success("Course published");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const deleteCourse = async (id) => {
    try { await api.delete(`/courses/${id}`); toast.success("Course removed"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const updateStatus = async (appId, status) => {
    try { await api.put(`/applications/${appId}/status`, { status }); toast.success(`Marked ${status.replace("_", " ")}`); load(); }
    catch { toast.error("Failed to update"); }
  };

  if (user?.role === "institute" && !user.institute_id) {
    return (
      <div>
        <Navbar />
        <ClaimInstitute />
        <Footer />
      </div>
    );
  }

  const stats = courses ? [
    { icon: BookOpen, label: "Active Courses", value: courses.length },
    { icon: Users, label: "Applications", value: apps.length },
    { icon: CheckCircle2, label: "Accepted", value: apps.filter((a) => a.status === "accepted").length },
    { icon: Wallet, label: "Fees Collected", value: inr(apps.filter((a) => a.payment_status === "paid").reduce((s, a) => s + (a.application_fee || 0), 0)) },
  ] : [];

  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Institute Portal</h1>
            <p className="mt-2 text-muted-foreground">Manage your courses and applicant pipeline.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-course-btn"><Plus className="mr-2 h-4 w-4" /> New Course</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="course-form-dialog">
              <DialogHeader><DialogTitle>Register a new course</DialogTitle></DialogHeader>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><Label>Course title</Label><Input data-testid="course-title" value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
                <div><Label>Specialty</Label><Input data-testid="course-specialty" value={form.specialty} onChange={(e) => set("specialty", e.target.value)} placeholder="Emergency Medicine" /></div>
                <div><Label>Format</Label>
                  <Select value={form.format} onValueChange={(v) => set("format", v)}>
                    <SelectTrigger data-testid="course-format"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Offline", "Hybrid", "Online"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>City</Label><Input data-testid="course-city" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
                <div><Label>State</Label><Input data-testid="course-state" value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
                <div><Label>Duration</Label><Input data-testid="course-duration" value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder="12 months" /></div>
                <div><Label>Seats</Label><Input data-testid="course-seats" type="number" value={form.seats} onChange={(e) => set("seats", e.target.value)} /></div>
                <div><Label>Course fee (₹)</Label><Input data-testid="course-fee" type="number" value={form.fee} onChange={(e) => set("fee", e.target.value)} /></div>
                <div><Label>Application fee (₹)</Label><Input data-testid="course-appfee" type="number" value={form.application_fee} onChange={(e) => set("application_fee", e.target.value)} /></div>
                <div><Label>Stipend</Label><Input data-testid="course-stipend" value={form.stipend} onChange={(e) => set("stipend", e.target.value)} placeholder="₹30,000/month" /></div>
                <div><Label>Application deadline</Label><Input data-testid="course-deadline" value={form.application_deadline} onChange={(e) => set("application_deadline", e.target.value)} placeholder="2026-08-01" /></div>
                <div className="sm:col-span-2"><Label>Eligibility (comma separated)</Label><Input data-testid="course-eligibility" value={form.eligibility} onChange={(e) => set("eligibility", e.target.value)} placeholder="MBBS, MD" /></div>
                <div className="sm:col-span-2"><Label>Tags (comma separated)</Label><Input data-testid="course-tags" value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="Trauma, Critical Care" /></div>
                <div className="sm:col-span-2"><Label>Description</Label><Textarea data-testid="course-description" value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="save-course-btn" onClick={saveCourse} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish course"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-2xl border border-border bg-card p-5">
              <s.icon className="h-5 w-5 text-primary" />
              <p className="mt-3 font-mono-nums text-2xl font-extrabold">{s.value}</p>
              <p className="text-sm text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="applicants" className="mt-10">
          <TabsList>
            <TabsTrigger value="applicants" data-testid="tab-applicants">Applicant Pipeline</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses">My Courses</TabsTrigger>
          </TabsList>

          <TabsContent value="applicants" className="pt-4">
            {apps.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground">No applications yet.</div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Applicant</TableHead><TableHead>Course</TableHead><TableHead>Payment</TableHead>
                      <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apps.map((a) => (
                      <TableRow key={a.id} data-testid={`applicant-row-${a.id}`}>
                        <TableCell>
                          <div className="font-semibold">{a.applicant?.name}</div>
                          <div className="text-xs text-muted-foreground">{a.applicant?.profession} · {a.applicant?.email}</div>
                        </TableCell>
                        <TableCell className="text-sm">{a.course?.title}</TableCell>
                        <TableCell><StatusBadge status={a.payment_status} /></TableCell>
                        <TableCell><StatusBadge status={a.status} /></TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {NEXT[a.status] && (
                              <Button size="sm" variant="outline" data-testid={`advance-btn-${a.id}`} onClick={() => updateStatus(a.id, NEXT[a.status])}>
                                {NEXT[a.status] === "in_review" ? "Review" : NEXT[a.status] === "interview" ? "Interview" : "Accept"}
                              </Button>
                            )}
                            {a.status !== "rejected" && a.status !== "accepted" && (
                              <Button size="sm" variant="ghost" className="text-destructive" data-testid={`reject-btn-${a.id}`} onClick={() => updateStatus(a.id, "rejected")}>Reject</Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="courses" className="pt-4">
            {courses === null ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {courses.map((c) => (
                  <div key={c.id} data-testid={`inst-course-${c.id}`} className="rounded-2xl border border-border bg-card p-5">
                    <div className="flex items-start justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wider text-accent">{c.specialty}</span>
                      <button data-testid={`delete-course-${c.id}`} onClick={() => deleteCourse(c.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <h3 className="mt-1 font-heading font-bold">{c.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{c.city} · {c.format}</p>
                    <p className="mt-2 font-mono-nums font-semibold">{inr(c.fee)}</p>
                  </div>
                ))}
                {courses.length === 0 && <p className="text-muted-foreground">No courses yet. Click "New Course" to add one.</p>}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
